import type { IdpV2Category, IdpV2Endpoint, IdpV2EndpointKind } from '../models/idp-v2-endpoint.model';

const API_BASE = '/api/v1/idp-v2';
const PARSE_SLUG = 'parse';
const KIND_PARSE: IdpV2EndpointKind = 'parse';
const KIND_PROCESS: IdpV2EndpointKind = 'process';

type EndpointDef = {
  slug: string;
  name: string;
  description: string;
  category: IdpV2Category;
  docTypeKey?: string;
};

type EndpointTuple = readonly [slug: string, name: string, description: string, docTypeKey?: string];

function ep({ slug, name, description, category, docTypeKey }: EndpointDef): IdpV2Endpoint {
  return {
    id: slug,
    slug,
    apiPath: `${API_BASE}/${slug}`,
    docTypeKey,
    name,
    description,
    category,
    kind: slug === PARSE_SLUG ? KIND_PARSE : KIND_PROCESS,
  };
}

function endpointsFor(category: IdpV2Category, items: readonly EndpointTuple[]): IdpV2Endpoint[] {
  return items.map(([slug, name, description, docTypeKey]) =>
    ep({ slug, name, description, category, docTypeKey }),
  );
}

function matchesQuery(endpoint: IdpV2Endpoint, query: string): boolean {
  return (
    endpoint.name.toLowerCase().includes(query) ||
    endpoint.slug.toLowerCase().includes(query) ||
    endpoint.description.toLowerCase().includes(query) ||
    (endpoint.docTypeKey?.toLowerCase().includes(query) ?? false)
  );
}

/** All production IDP v2 endpoints — mirrors backend route registry. */
export const IDP_V2_ENDPOINTS: IdpV2Endpoint[] = [
  ep({
    slug: PARSE_SLUG,
    name: 'Parse (raw OCR)',
    description:
      'Validate, classify document format, and return raw parse output without structured extraction.',
    category: 'custom',
    docTypeKey: 'CUSTOM_PARSER',
  }),

  // Identity & KYC (6)
  ...endpointsFor('identity_kyc', [
    [
      'aadhaar-card',
      'Aadhaar Card',
      'Validate → OCR → verify Aadhaar card → extract structured fields.',
      'AADHAAR',
    ],
    [
      'pan-card',
      'PAN Card',
      'Validate → OCR → verify PAN card → extract structured fields.',
      'PAN_CARD',
    ],
    [
      'passport',
      'Passport',
      'Validate → OCR → verify passport → extract structured fields.',
      'PASSPORT',
    ],
    [
      'driving-license',
      'Driving License',
      'Validate → OCR → verify driving license → extract structured fields.',
      'DRIVING_LICENSE',
    ],
    [
      'voter-id',
      'Voter ID',
      'Validate → OCR → verify voter ID / EPIC → extract structured fields.',
      'VOTER_ID',
    ],
    [
      'gst-certificate',
      'GST Certificate',
      'Validate → OCR → verify GST registration certificate → extract structured fields.',
      'GST_CERTIFICATE',
    ],
  ]),

  // Financial — Income & Tax (14)
  ...endpointsFor('financial_income_tax', [
    [
      'bank-statement',
      'Bank Statement',
      'Process a bank account statement with transaction details.',
      'BANK_STATEMENT',
    ],
    ['itr-v', 'ITR-V', 'Process an ITR-V verification / acknowledgement form.', 'ITR_V'],
    ['itr-full', 'ITR Full', 'Process a full income tax return filing.', 'ITR_FULL'],
    [
      'form-16-part-a',
      'Form 16 Part A',
      'Process Form 16 Part A (TDS certificate).',
      'FORM_16_PART_A',
    ],
    [
      'form-16-part-b',
      'Form 16 Part B',
      'Process Form 16 Part B (salary annexure).',
      'FORM_16_PART_B',
    ],
    ['form-26as', 'Form 26AS', 'Process Form 26AS annual tax statement.', 'FORM_26AS'],
    ['ais', 'AIS', 'Process Annual Information Statement.', 'AIS'],
    ['tis', 'TIS', 'Process Taxpayer Information Summary.', 'TIS'],
    ['gstr-3b', 'GSTR-3B', 'Process GSTR-3B monthly GST return.', 'GSTR_3B'],
    ['gstr-9', 'GSTR-9', 'Process GSTR-9 annual GST return.', 'GSTR_9'],
    ['gstr-1', 'GSTR-1', 'Process GSTR-1 outward supplies return.', 'GSTR_1'],
    ['salary-slip', 'Salary Slip', 'Process monthly salary / pay slip.', 'SALARY_SLIP'],
    [
      'employer-letter',
      'Employer Letter',
      'Process employer verification or employment letter.',
      'EMPLOYER_LETTER',
    ],
    [
      'appointment-letter',
      'Appointment Letter',
      'Process appointment / offer letter.',
      'APPOINTMENT_LETTER',
    ],
  ]),

  // Financial — Wealth & Obligations (9)
  ...endpointsFor('financial_wealth_obligations', [
    [
      'mutual-fund-statement',
      'Mutual Fund Statement',
      'Process mutual fund account statement.',
      'MUTUAL_FUND_STATEMENT',
    ],
    [
      'demat-holding-statement',
      'Demat Holding Statement',
      'Process demat securities holding statement.',
      'DEMAT_HOLDING_STATEMENT',
    ],
    [
      'credit-card-statement',
      'Credit Card Statement',
      'Process credit card billing statement.',
      'CREDIT_CARD_STATEMENT',
    ],
    [
      'loan-account-statement',
      'Loan Account Statement',
      'Process loan account statement.',
      'LOAN_ACCOUNT_STATEMENT',
    ],
    [
      'fixed-deposit-certificate',
      'Fixed Deposit Certificate',
      'Process fixed deposit certificate.',
      'FIXED_DEPOSIT_CERTIFICATE',
    ],
    [
      'insurance-policy',
      'Insurance Policy',
      'Process insurance policy document.',
      'INSURANCE_POLICY',
    ],
    ['rent-receipt', 'Rent Receipt', 'Process rent payment receipt.', 'RENT_RECEIPT'],
    [
      'property-tax-receipt',
      'Property Tax Receipt',
      'Process property tax payment receipt.',
      'PROPERTY_TAX_RECEIPT',
    ],
    ['light-bill', 'Light Bill', 'Process electricity / light bill.', 'LIGHT_BILL'],
  ]),

  // Financial — SME / Business (8)
  ...endpointsFor('financial_sme_business', [
    [
      'profit-and-loss-statement',
      'Profit & Loss Statement',
      'Process profit and loss statement.',
      'PROFIT_AND_LOSS_STATEMENT',
    ],
    ['balance-sheet', 'Balance Sheet', 'Process balance sheet.', 'BALANCE_SHEET'],
    ['trial-balance', 'Trial Balance', 'Process trial balance report.', 'TRIAL_BALANCE'],
    [
      'ca-certified-financials',
      'CA Certified Financials',
      'Process CA-certified financial statements.',
      'CA_CERTIFIED_FINANCIALS',
    ],
    ['audit-report', 'Audit Report', 'Process statutory audit report.', 'AUDIT_REPORT'],
    [
      'stock-statement',
      'Stock Statement',
      'Process inventory / stock statement.',
      'STOCK_STATEMENT',
    ],
    [
      'debtors-creditors-list',
      'Debtors & Creditors List',
      'Process debtors and creditors listing.',
      'DEBTORS_CREDITORS_LIST',
    ],
    [
      'gstr-2a-2b',
      'GSTR-2A / 2B',
      'Process GSTR-2A or GSTR-2B reconciliation data.',
      'GSTR_2A_2B',
    ],
  ]),

  // Property & Collateral (8)
  ...endpointsFor('property_collateral', [
    ['sale-deed', 'Sale Deed', 'Process property sale deed.', 'SALE_DEED'],
    [
      'mother-deed',
      'Mother Deed',
      'Process mother deed / title chain document.',
      'MOTHER_DEED',
    ],
    [
      'encumbrance-certificate',
      'Encumbrance Certificate',
      'Process encumbrance certificate (EC).',
      'ENCUMBRANCE_CERTIFICATE',
    ],
    [
      'khata-certificate',
      'Khata Certificate',
      'Process khata / property tax assessment certificate.',
      'KHATA_CERTIFICATE',
    ],
    [
      'sanctioned-plan',
      'Sanctioned Plan',
      'Process sanctioned building plan.',
      'SANCTIONED_PLAN',
    ],
    [
      'noc-society-builder',
      'NOC (Society / Builder)',
      'Process society or builder NOC.',
      'NOC_SOCIETY_BUILDER',
    ],
    [
      'rera-registration',
      'RERA Registration',
      'Process RERA registration certificate.',
      'RERA_REGISTRATION',
    ],
    [
      'valuation-report',
      'Valuation Report',
      'Process property valuation report.',
      'VALUATION_REPORT',
    ],
  ]),
];

export function getEndpointBySlug(slug: string): IdpV2Endpoint | undefined {
  return IDP_V2_ENDPOINTS.find((endpoint) => endpoint.slug === slug);
}

export function getEndpointsByCategory(category: IdpV2Category): IdpV2Endpoint[] {
  return IDP_V2_ENDPOINTS.filter((endpoint) => endpoint.category === category);
}

export function filterEndpoints(query: string): IdpV2Endpoint[] {
  const normalized = query.trim().toLowerCase();
  if (!normalized) {
    return IDP_V2_ENDPOINTS;
  }
  return IDP_V2_ENDPOINTS.filter((endpoint) => matchesQuery(endpoint, normalized));
}
